import React, { useState, useEffect, useCallback } from "react";
import { Card, Form, Input, InputNumber, Button, Row, Col, Typography, Flex, Checkbox, Table, message, Spin, Upload, Space, Tag, Radio, ColorPicker, Select } from "antd";
import { SaveOutlined, PlusOutlined, DeleteOutlined, FileProtectOutlined, AppstoreOutlined, BankOutlined, UploadOutlined, PictureOutlined, EditOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { getCookie } from "../../utils/cookieUtils";
import { amountFormatter, amountParser } from "../../utils/formatters";
import { getStateInfoFromGstin, GST_STATE_OPTIONS, GST_STATE_MAP, getStateCodeFromName } from "../../utils/gstUtils";
import { API_BASE_URL } from "../../config/api";
import { getCompanySettings, updateCompanySettings } from "../../services/organizationService";

const { Title, Text, Paragraph } = Typography;

const resolveAssetUrl = (url) => {
  if (!url || typeof url !== "string") return "";
  const trimmed = url.trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://") || trimmed.startsWith("data:")) {
    return trimmed;
  }
  if (trimmed.startsWith("/")) {
    return `${API_BASE_URL}${trimmed}`;
  }
  return `${API_BASE_URL}/${trimmed}`;
};

const populateInvoiceForm = (form, s, setters) => {
  if (!s) return;
  const { setCompanyLogo, setCompanySeal, setQuotationHeroBanner, setQuotationFooterBanner, setLogoError, setSealError, setHeroBannerError, setFooterBannerError, setServices } = setters;

  form.setFieldsValue({
    companyName: s.companyName || "",
    shouldShowCompanyNameOnInvoice: s.shouldShowCompanyNameOnInvoice ?? true,
    gstNumber: s.gstNumber || "",
    gstPercentage: s.gstPercentage !== undefined ? s.gstPercentage : 18,
    stateName: s.stateName || s.state || (s.gstNumber ? getStateInfoFromGstin(s.gstNumber).stateName : "") || "Tamil Nadu",
    stateCode: s.stateCode || (s.gstNumber ? getStateInfoFromGstin(s.gstNumber).stateCode : "") || "33",
    invoiceRegisteredAddress: s.invoiceRegisteredAddress || s.companyAddress || "",
    invoiceFooter: s.invoiceFooter || "Thank you for your business.",
    invoiceTerms: s.invoiceTerms || "(i) All rates quoted are valid for 14 days.\n(ii) 40% payment should be done in advance.\n(iii) The remaining amount should be paid within 7 days of invoice.",
    quotationBannerText: s.quotationBannerText || "Think Software,\nThink Softrate.",
    quotationBannerTextColor: s.quotationBannerTextColor || "#ffffff",
    quotationBannerBgColor: s.quotationBannerBgColor || "#785b18",
    quotationNote: s.quotationNote || "We aim to provide the best software to automate your business (Our charges are 50% less than market price with a mantra “High quality at affordable cost”).\nThe price mentioned above is exclusive of applicable tax.",
    bankName: s.bankDetails?.bankName || "",
    accountNumber: s.bankDetails?.accountNumber || "",
    ifscCode: s.bankDetails?.ifscCode || "",
    branchName: s.bankDetails?.branchName || "",
    bankName2: s.bankDetails2?.bankName || "",
    accountNumber2: s.bankDetails2?.accountNumber || "",
    ifscCode2: s.bankDetails2?.ifscCode || "",
    branchName2: s.bankDetails2?.branchName || "",
  });

  if (setters.setQuotationBannerTextColor) {
    setters.setQuotationBannerTextColor(s.quotationBannerTextColor || "#ffffff");
  }
  if (setters.setQuotationBannerBgColor) {
    setters.setQuotationBannerBgColor(s.quotationBannerBgColor || "#785b18");
  }
  if (setters.setQuotationBannerBgImage) {
    setters.setQuotationBannerBgImage(s.quotationBannerBgImage || "");
  }
  if (setters.setBannerBgType) {
    setters.setBannerBgType(s.quotationBannerBgImage ? "image" : "color");
  }

  if (s.invoiceLogo) {
    setCompanyLogo(s.invoiceLogo);
    setLogoError(false);
  }
  if (s.invoiceSeal) {
    setCompanySeal(s.invoiceSeal);
    setSealError(false);
  }
  if (s.quotationHeroBanner) {
    setQuotationHeroBanner(s.quotationHeroBanner);
    setHeroBannerError(false);
  }
  if (s.quotationFooterBanner) {
    setQuotationFooterBanner(s.quotationFooterBanner);
    setFooterBannerError(false);
  }
  if (Array.isArray(s.products)) {
    setServices(
      s.products.map((p, idx) => ({
        id: p._id || p.id || `p-${idx + 1}-${Date.now()}`,
        name: p.name,
        sacHsn: p.sacHsn || "—",
        minPrice: p.minPrice || p.price || 0,
        maxPrice: p.maxPrice || p.price || 0,
        tags: p.tags || [],
        subProducts: Array.isArray(p.subProducts)
          ? p.subProducts.map((sp, sidx) => ({
              id: sp.id || sp._id || `sp-${idx}-${sidx}-${Date.now()}`,
              name: sp.name,
              sacHsn: sp.sacHsn || p.sacHsn || "—",
              minPrice: sp.minPrice || sp.price || 0,
              maxPrice: sp.maxPrice || sp.price || 0,
              price: sp.price || sp.minPrice || 0,
              description: sp.description || "",
            }))
          : [],
      })),
    );
  }
};

const compressImageFile = (file, maxWidth = 1200, maxHeight = 600, quality = 0.82) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        let width = img.width;
        let height = img.height;
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
        if (height > maxHeight) {
          width = Math.round((width * maxHeight) / height);
          height = maxHeight;
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL("image/jpeg", quality);
        resolve(dataUrl);
      };
      img.onerror = (err) => reject(err);
    };
    reader.onerror = (err) => reject(err);
    reader.readAsDataURL(file);
  });
};

const handleImageFileReader = (file, maxMb, setter, errorSetter, successMsg) => {
  if (!file) return;
  if (file.size > maxMb * 1024 * 1024) {
    message.error(`File size must be under ${maxMb}MB.`);
    return;
  }
  const reader = new FileReader();
  reader.onload = (uploadEvent) => {
    setter(uploadEvent.target.result);
    errorSetter(false);
    message.success(successMsg);
  };
  reader.readAsDataURL(file);
};

function SubProductsNestedView({ service, onAddSubProduct, onDeleteSubProduct }) {
  const [subName, setSubName] = useState("");
  const [subHsn, setSubHsn] = useState(service.sacHsn !== "—" ? service.sacHsn : "");
  const [subMinPrice, setSubMinPrice] = useState(null);
  const [subMaxPrice, setSubMaxPrice] = useState(null);

  const handleAdd = () => {
    if (!subName.trim()) {
      message.warning("Please enter a sub-product name.");
      return;
    }
    onAddSubProduct(service.id, {
      id: `sp-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
      name: subName.trim(),
      sacHsn: subHsn.trim() || (service.sacHsn !== "—" ? service.sacHsn : ""),
      minPrice: Number(subMinPrice) || 0,
      maxPrice: Number(subMaxPrice) || 0,
      price: Number(subMinPrice) || 0,
      description: "",
    });
    setSubName("");
    setSubMinPrice(null);
    setSubMaxPrice(null);
  };

  const subColumns = [
    {
      title: "Sub-Product / Variation",
      dataIndex: "name",
      key: "name",
      render: (text) => <span style={{ fontWeight: 600, color: "#1e293b", fontSize: "0.82rem" }}>↳ {text}</span>,
    },
    {
      title: "HSN / SAC",
      dataIndex: "sacHsn",
      key: "sacHsn",
      render: (code) => <Tag color="cyan">{code || "—"}</Tag>,
    },
    {
      title: "Rate / Range",
      key: "price",
      render: (_, sp) => (
        <span style={{ color: "#0f172a", fontWeight: 500, fontSize: "0.82rem" }}>
          ₹{Number(sp.minPrice || sp.price || 0).toLocaleString()}
          {sp.maxPrice > (sp.minPrice || 0) ? ` – ₹${Number(sp.maxPrice).toLocaleString()}` : ""}
        </span>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 70,
      render: (_, sp) => <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => onDeleteSubProduct(service.id, sp.id)} />,
    },
  ];

  const subList = service.subProducts || [];

  return (
    <div style={{ padding: "14px 18px", background: "#f8fafc", borderRadius: 8, border: "1px dashed #cbd5e1" }}>
      <Flex vertical gap={12}>
        <Flex align="center" justify="space-between">
          <Text strong style={{ fontSize: "0.82rem", color: "#334155" }}>
            Sub-Products under &ldquo;{service.name}&rdquo; ({subList.length})
          </Text>
          <Text type="secondary" style={{ fontSize: "0.72rem" }}>
            These appear as nested selectable menu items in Invoices, Quotations, and Deals.
          </Text>
        </Flex>

        {/* Inline Add Sub-Product */}
        <Row gutter={[8, 8]} align="middle">
          <Col xs={24} sm={8}>
            <Input size="middle" placeholder="Sub-product name (e.g. 5 User Pack, Tier 1)" value={subName} onChange={(e) => setSubName(e.target.value)} />
          </Col>
          <Col xs={12} sm={4}>
            <Input size="middle" placeholder="HSN/SAC" value={subHsn} onChange={(e) => setSubHsn(e.target.value)} />
          </Col>
          <Col xs={12} sm={4}>
            <InputNumber size="middle" min={0} placeholder="Min ₹" value={subMinPrice ?? undefined} onChange={setSubMinPrice} formatter={amountFormatter} parser={amountParser} style={{ width: "100%" }} />
          </Col>
          <Col xs={12} sm={4}>
            <InputNumber size="middle" min={0} placeholder="Max ₹" value={subMaxPrice ?? undefined} onChange={setSubMaxPrice} formatter={amountFormatter} parser={amountParser} style={{ width: "100%" }} />
          </Col>
          <Col xs={24} sm={4}>
            <Button size="middle" type="primary" icon={<PlusOutlined />} onClick={handleAdd} block>
              Sub-Product
            </Button>
          </Col>
        </Row>

        {subList.length > 0 ? <Table columns={subColumns} dataSource={subList} rowKey="id" pagination={false} size="small" style={{ background: "#ffffff", borderRadius: 6 }} /> : <div style={{ padding: "6px 0", color: "#94a3b8", fontSize: "0.78rem" }}>No sub-products yet. Add sub-products above to enable nested dropdown selections.</div>}
      </Flex>
    </div>
  );
}

function InvoiceSettings() {
  const reduxCompanyCode = useSelector((state) => state.executor?.companyInfo?.companyCode);
  const user = useSelector((state) => state.session?.settings?.userInfo);
  const effectiveCompanyCode = reduxCompanyCode || user?.companyCode || getCookie("companyCode") || "";

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [companyLogo, setCompanyLogo] = useState("");
  const [companySeal, setCompanySeal] = useState("");
  const [quotationHeroBanner, setQuotationHeroBanner] = useState("");
  const [quotationFooterBanner, setQuotationFooterBanner] = useState("");
  const [quotationBannerTextColor, setQuotationBannerTextColor] = useState("#ffffff");
  const [quotationBannerBgColor, setQuotationBannerBgColor] = useState("#785b18");
  const [quotationBannerBgImage, setQuotationBannerBgImage] = useState("");
  const [bannerBgType, setBannerBgType] = useState("color");
  const quotationBannerTextWatched = Form.useWatch("quotationBannerText", form);
  const [logoError, setLogoError] = useState(false);
  const [sealError, setSealError] = useState(false);
  const [heroBannerError, setHeroBannerError] = useState(false);
  const [footerBannerError, setFooterBannerError] = useState(false);
  const [services, setServices] = useState([]);
  const [newServiceName, setNewServiceName] = useState("");
  const [newMinPrice, setNewMinPrice] = useState(null);
  const [newMaxPrice, setNewMaxPrice] = useState(null);
  const [newSacHsn, setNewSacHsn] = useState("");
  const [editingServiceId, setEditingServiceId] = useState(null);

  const handleBannerBgImageUpload = async (file) => {
    if (!file) return;
    try {
      message.loading({ content: "Compressing image...", key: "banner-img-compress" });
      const compressedDataUrl = await compressImageFile(file, 1200, 600, 0.82);
      setQuotationBannerBgImage(compressedDataUrl);
      setBannerBgType("image");
      message.success({ content: "Quotation banner image compressed & ready to save!", key: "banner-img-compress" });
    } catch (err) {
      console.error("Compression failed:", err);
      message.error({ content: "Failed to compress banner image.", key: "banner-img-compress" });
    }
  };

  const loadSettings = useCallback(async () => {
    if (!effectiveCompanyCode) return;
    setLoading(true);
    try {
      const res = await getCompanySettings(effectiveCompanyCode);
      if (res?.success && res.settings) {
        populateInvoiceForm(form, res.settings, {
          setCompanyLogo,
          setCompanySeal,
          setQuotationHeroBanner,
          setQuotationFooterBanner,
          setQuotationBannerTextColor,
          setQuotationBannerBgColor,
          setQuotationBannerBgImage,
          setBannerBgType,
          setLogoError,
          setSealError,
          setHeroBannerError,
          setFooterBannerError,
          setServices,
        });
      }
    } catch (err) {
      console.error("Failed to load invoice settings:", err);
    } finally {
      setLoading(false);
    }
  }, [effectiveCompanyCode, form]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleLogoFile = (file) => handleImageFileReader(file, 3, setCompanyLogo, setLogoError, "Company logo loaded. Click 'Save All Changes' to persist.");

  const handleSealFile = (file) => handleImageFileReader(file, 3, setCompanySeal, setSealError, "Company seal loaded. Click 'Save All Changes' to persist.");

  const handleHeroBannerFile = (file) => handleImageFileReader(file, 3, setQuotationHeroBanner, setHeroBannerError, "Quotation header banner loaded. Click 'Save All Changes' to persist.");

  const handleFooterBannerFile = (file) => handleImageFileReader(file, 3, setQuotationFooterBanner, setFooterBannerError, "Quotation footer banner loaded. Click 'Save All Changes' to persist.");

  const handleAddService = () => {
    if (!newServiceName.trim()) {
      message.warning("Please enter a service name.");
      return;
    }

    if (editingServiceId) {
      setServices(
        services.map((s) =>
          s.id === editingServiceId
            ? {
                ...s,
                name: newServiceName.trim(),
                sacHsn: newSacHsn.trim() || "—",
                minPrice: Number(newMinPrice) || 0,
                maxPrice: Number(newMaxPrice) || 0,
              }
            : s,
        ),
      );
      setEditingServiceId(null);
      message.success("Service updated successfully.");
    } else {
      const newService = {
        id: `p-${Date.now()}`,
        name: newServiceName.trim(),
        sacHsn: newSacHsn.trim() || "—",
        minPrice: Number(newMinPrice) || 0,
        maxPrice: Number(newMaxPrice) || 0,
        tags: [],
        subProducts: [],
      };
      setServices([...services, newService]);
      message.success("Service added to list. Click Save to persist.");
    }

    setNewServiceName("");
    setNewMinPrice(null);
    setNewMaxPrice(null);
    setNewSacHsn("");
  };

  const handleEditService = (service) => {
    setEditingServiceId(service.id);
    setNewServiceName(service.name);
    setNewSacHsn(service.sacHsn !== "—" ? service.sacHsn : "");
    setNewMinPrice(service.minPrice);
    setNewMaxPrice(service.maxPrice);
  };

  const handleDeleteService = (id) => {
    setServices(services.filter((s) => s.id !== id));
    message.success("Service removed from list. Click Save to persist.");
  };

  const handleAddSubProduct = (parentId, subProduct) => {
    setServices((prev) =>
      prev.map((s) => {
        if (s.id === parentId) {
          const existingSubs = s.subProducts || [];
          return {
            ...s,
            subProducts: [...existingSubs, subProduct],
          };
        }
        return s;
      }),
    );
    message.success(`Sub-product "${subProduct.name}" added. Click Save to persist.`);
  };

  const handleDeleteSubProduct = (parentId, subProductId) => {
    setServices((prev) =>
      prev.map((s) => {
        if (s.id === parentId) {
          return {
            ...s,
            subProducts: (s.subProducts || []).filter((sp) => sp.id !== subProductId),
          };
        }
        return s;
      }),
    );
    message.success("Sub-product removed. Click Save to persist.");
  };

  const handleSaveAll = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const payload = {
        companyName: values.companyName,
        invoiceLogo: companyLogo,
        invoiceSeal: companySeal,
        quotationHeroBanner,
        quotationFooterBanner,
        quotationBannerText: values.quotationBannerText || "Think Software,\nThink Softrate.",
        quotationBannerTextColor: quotationBannerTextColor || "#ffffff",
        quotationBannerBgColor: quotationBannerBgColor || "#785b18",
        quotationBannerBgImage: bannerBgType === "image" ? quotationBannerBgImage : "",
        quotationNote: values.quotationNote || "We aim to provide the best software to automate your business (Our charges are 50% less than market price with a mantra “High quality at affordable cost”).\nThe price mentioned above is exclusive of applicable tax.",
        shouldShowCompanyNameOnInvoice: values.shouldShowCompanyNameOnInvoice,
        gstNumber: values.gstNumber,
        gstPercentage: values.gstPercentage,
        stateName: values.stateName || "Tamil Nadu",
        stateCode: values.stateCode || "33",
        state: values.stateName || "Tamil Nadu",
        invoiceRegisteredAddress: values.invoiceRegisteredAddress,
        invoiceFooter: values.invoiceFooter,
        invoiceTerms: values.invoiceTerms,
        bankDetails: {
          bankName: values.bankName || "",
          accountNumber: values.accountNumber || "",
          ifscCode: values.ifscCode || "",
          branchName: values.branchName || "",
        },
        bankDetails2: {
          bankName: values.bankName2 || "",
          accountNumber: values.accountNumber2 || "",
          ifscCode: values.ifscCode2 || "",
          branchName: values.branchName2 || "",
        },
        products: services.map((s) => ({
          name: s.name,
          sacHsn: s.sacHsn,
          minPrice: s.minPrice,
          maxPrice: s.maxPrice,
          tags: s.tags || [],
          subProducts: (s.subProducts || []).map((sp) => ({
            id: sp.id,
            name: sp.name,
            sacHsn: sp.sacHsn,
            minPrice: sp.minPrice,
            maxPrice: sp.maxPrice,
            price: sp.price || sp.minPrice || 0,
            description: sp.description || "",
          })),
        })),
      };

      const res = await updateCompanySettings(effectiveCompanyCode, payload);
      if (res?.success !== false) {
        message.success("All invoice, branding, and bank settings saved successfully!");
      } else {
        message.error(res?.message || "Failed to save invoice settings.");
      }
    } catch (err) {
      console.error(err);
      message.error("Failed to save settings.");
    } finally {
      setSaving(false);
    }
  };

  const serviceColumns = [
    {
      title: "Service / Product Description",
      dataIndex: "name",
      key: "name",
      render: (text, r) => (
        <Flex vertical gap={4}>
          <Text strong style={{ color: "#0f172a", fontSize: "0.88rem" }}>
            {text}
          </Text>
          {r.subProducts && r.subProducts.length > 0 && (
            <Tag color="geekblue" style={{ width: "fit-content", fontSize: "0.72rem" }}>
              {r.subProducts.length} {r.subProducts.length === 1 ? "sub-product" : "sub-products"}
            </Tag>
          )}
        </Flex>
      ),
    },
    {
      title: "HSN / SAC",
      dataIndex: "sacHsn",
      key: "sacHsn",
      render: (code) => <Tag color="blue">{code || "—"}</Tag>,
    },
    {
      title: "Standard Rate / Range",
      key: "priceRange",
      render: (_, r) => (
        <span>
          ₹{Number(r.minPrice).toLocaleString()} {r.maxPrice > r.minPrice ? `– ₹${Number(r.maxPrice).toLocaleString()}` : ""}
        </span>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      render: (_, r) => (
        <Flex gap={8}>
          <Button type="text" style={{ color: "#2563eb" }} icon={<EditOutlined />} onClick={() => handleEditService(r)} />
          <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleDeleteService(r.id)} />
        </Flex>
      ),
    },
  ];

  return (
    <div className="invoice-settings-container" style={{ maxWidth: 1200, margin: "0 auto", padding: "24px 16px" }}>
      <Flex justify="space-between" align="center" style={{ marginBottom: 24 }} wrap="wrap" gap={12}>
        <div>
          <Title level={3} style={{ margin: 0, color: "#0f172a", fontWeight: 700 }}>
            Invoice & Document Branding Settings
          </Title>
          <Paragraph type="secondary" style={{ margin: "4px 0 0", fontSize: "0.88rem" }}>
            Configure default company info, logos, signature stamps, tax rates, bank accounts, and service catalogs.
          </Paragraph>
        </div>
        <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSaveAll} size="large" style={{ background: "#2563eb", borderRadius: 8, fontWeight: 600, padding: "0 24px" }}>
          Save All Changes
        </Button>
      </Flex>

      <Spin spinning={loading} tip="Loading settings...">
        <Form form={form} layout="vertical" initialValues={{ stateName: "Tamil Nadu", stateCode: "33", gstPercentage: 18, shouldShowCompanyNameOnInvoice: true }}>
          <Row gutter={[20, 20]}>
            <Col xs={24} lg={12}>
              <Card
                className="org-card"
                styles={{ body: { padding: 24 } }}
                title={
                  <Flex align="center" gap={10}>
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 8,
                        background: "#eff6ff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#2563eb",
                      }}
                    >
                      <PictureOutlined style={{ fontSize: 18 }} />
                    </div>
                    <span style={{ fontSize: "1.05rem", fontWeight: 700, color: "#0f172a" }}>Brand Assets & Identity</span>
                  </Flex>
                }
              >
                <Flex vertical gap={18}>
                  <div>
                    <Text style={{ fontSize: "0.75rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", display: "block", marginBottom: 8 }}>Company Logo (400 × 400 px)</Text>
                    <Flex align="center" gap={16}>
                      <div
                        style={{
                          width: 100,
                          height: 100,
                          borderRadius: 12,
                          background: "#f8fafc",
                          border: "2px dashed #cbd5e1",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                          padding: 8,
                          flexShrink: 0,
                        }}
                      >
                        {resolveAssetUrl(companyLogo) && !logoError ? (
                          <img src={resolveAssetUrl(companyLogo)} alt="Company Logo" onError={() => setLogoError(true)} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                        ) : (
                          <Text type="secondary" style={{ fontSize: "0.72rem", textAlign: "center" }}>
                            No Logo
                          </Text>
                        )}
                      </div>
                      <div style={{ flex: 1 }}>
                        <Paragraph type="secondary" style={{ fontSize: "0.8rem", marginBottom: 10, lineHeight: 1.4 }}>
                          Upload your high-res company logo (400 × 400 px, PNG/JPG, max 3MB). Appears at the top-left of invoices & quotations.
                        </Paragraph>
                        <Space wrap>
                          <Upload
                            beforeUpload={(file) => {
                              handleLogoFile(file);
                              return false;
                            }}
                            showUploadList={false}
                            accept="image/*"
                          >
                            <Button icon={<UploadOutlined />} style={{ borderColor: "#2563eb", color: "#2563eb", fontWeight: 600 }}>
                              {companyLogo ? "Change Logo" : "Upload Logo"}
                            </Button>
                          </Upload>
                        </Space>
                      </div>
                    </Flex>
                  </div>

                  <div>
                    <Text style={{ fontSize: "0.75rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", display: "block", marginBottom: 8 }}>Company Seal / Stamp (600 × 400 px)</Text>
                    <Flex align="center" gap={16}>
                      <div
                        style={{
                          width: 100,
                          height: 100,
                          borderRadius: 12,
                          background: "#f8fafc",
                          border: "2px dashed #cbd5e1",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                          padding: 8,
                          flexShrink: 0,
                        }}
                      >
                        {resolveAssetUrl(companySeal) && !sealError ? (
                          <img src={resolveAssetUrl(companySeal)} alt="Company Seal" onError={() => setSealError(true)} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                        ) : (
                          <Text type="secondary" style={{ fontSize: "0.72rem", textAlign: "center" }}>
                            No Seal
                          </Text>
                        )}
                      </div>
                      <div style={{ flex: 1 }}>
                        <Paragraph type="secondary" style={{ fontSize: "0.8rem", marginBottom: 10, lineHeight: 1.4 }}>
                          Upload authorized signatory stamp (600 × 400 px, PNG/JPG, max 3MB). Appears at bottom-right of invoice.
                        </Paragraph>
                        <Space wrap>
                          <Upload
                            beforeUpload={(file) => {
                              handleSealFile(file);
                              return false;
                            }}
                            showUploadList={false}
                            accept="image/*"
                          >
                            <Button icon={<UploadOutlined />} style={{ borderColor: "#2563eb", color: "#2563eb", fontWeight: 600 }}>
                              {companySeal ? "Change Seal" : "Upload Seal"}
                            </Button>
                          </Upload>
                        </Space>
                      </div>
                    </Flex>
                  </div>

                  <div>
                    <Text style={{ fontSize: "0.75rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", display: "block", marginBottom: 8 }}>Quotation Header Banner (1920 × 500 px)</Text>
                    <Flex align="center" gap={16}>
                      <div
                        style={{
                          width: 140,
                          height: 70,
                          borderRadius: 10,
                          background: "#1e293b",
                          border: "2px dashed #cbd5e1",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                          padding: 4,
                          flexShrink: 0,
                        }}
                      >
                        {resolveAssetUrl(quotationHeroBanner) && !heroBannerError ? (
                          <img src={resolveAssetUrl(quotationHeroBanner)} alt="Quotation Header Banner" onError={() => setHeroBannerError(true)} style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 6 }} />
                        ) : (
                          <Text style={{ fontSize: "0.72rem", textAlign: "center", color: "#94a3b8" }}>No Header Banner</Text>
                        )}
                      </div>
                      <div style={{ flex: 1 }}>
                        <Paragraph type="secondary" style={{ fontSize: "0.8rem", marginBottom: 10, lineHeight: 1.4 }}>
                          Background banner for Quotation header (1920 × 500 px, PNG/JPG, max 3MB).
                        </Paragraph>
                        <Space wrap>
                          <Upload
                            beforeUpload={(file) => {
                              handleHeroBannerFile(file);
                              return false;
                            }}
                            showUploadList={false}
                            accept="image/*"
                          >
                            <Button icon={<UploadOutlined />} style={{ borderColor: "#2563eb", color: "#2563eb", fontWeight: 600 }}>
                              {quotationHeroBanner ? "Change Header Banner" : "Upload Header Banner"}
                            </Button>
                          </Upload>
                        </Space>
                      </div>
                    </Flex>
                  </div>

                  <div>
                    <Text style={{ fontSize: "0.75rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", display: "block", marginBottom: 8 }}>Quotation Footer / Partner Strip (600 × 100 px)</Text>
                    <Flex align="center" gap={16}>
                      <div
                        style={{
                          width: 140,
                          height: 50,
                          borderRadius: 8,
                          background: "#f8fafc",
                          border: "2px dashed #cbd5e1",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                          padding: 4,
                          flexShrink: 0,
                        }}
                      >
                        {resolveAssetUrl(quotationFooterBanner) && !footerBannerError ? (
                          <img src={resolveAssetUrl(quotationFooterBanner)} alt="Quotation Footer Banner" onError={() => setFooterBannerError(true)} style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 6 }} />
                        ) : (
                          <Text type="secondary" style={{ fontSize: "0.72rem", textAlign: "center" }}>
                            No Footer Banner
                          </Text>
                        )}
                      </div>
                      <div style={{ flex: 1 }}>
                        <Paragraph type="secondary" style={{ fontSize: "0.8rem", marginBottom: 10, lineHeight: 1.4 }}>
                          Partner logos / trust bar at Quotation bottom (600 × 100 px, PNG/JPG, max 3MB).
                        </Paragraph>
                        <Space wrap>
                          <Upload
                            beforeUpload={(file) => {
                              handleFooterBannerFile(file);
                              return false;
                            }}
                            showUploadList={false}
                            accept="image/*"
                          >
                            <Button icon={<UploadOutlined />} style={{ borderColor: "#2563eb", color: "#2563eb", fontWeight: 600 }}>
                              {quotationFooterBanner ? "Change Footer Banner" : "Upload Footer Banner"}
                            </Button>
                          </Upload>
                        </Space>
                      </div>
                    </Flex>
                  </div>

                  <div>
                    <Flex justify="space-between" align="center" style={{ marginBottom: 6 }}>
                      <Text style={{ fontSize: "0.75rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>Company Name</Text>
                      <Form.Item name="shouldShowCompanyNameOnInvoice" valuePropName="checked" noStyle>
                        <Checkbox style={{ fontWeight: 600, fontSize: "0.82rem", color: "#0f172a" }}>Show on Documents</Checkbox>
                      </Form.Item>
                    </Flex>
                    <Form.Item name="companyName" noStyle>
                      <Input placeholder="Softrate Technologies Private Limited" size="large" style={{ borderRadius: 8 }} />
                    </Form.Item>
                  </div>
                </Flex>
              </Card>
            </Col>

            <Col xs={24} lg={12}>
              <Card
                className="org-card"
                styles={{ body: { padding: 24 } }}
                title={
                  <Flex align="center" gap={10}>
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 8,
                        background: "#eff6ff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#2563eb",
                      }}
                    >
                      <FileProtectOutlined style={{ fontSize: 18 }} />
                    </div>
                    <span style={{ fontSize: "1.05rem", fontWeight: 700, color: "#0f172a" }}>GST & Legal Info</span>
                  </Flex>
                }
              >
                <Flex vertical gap={14}>
                  <Row gutter={16}>
                    <Col span={14}>
                      <Form.Item label="GSTIN / Tax Number" name="gstNumber">
                        <Input
                          placeholder="33AABCT1332L1ZX"
                          onChange={(e) => {
                            const val = e.target.value?.toUpperCase().trim();
                            if (val && val.length >= 2) {
                              const derived = getStateInfoFromGstin(val);
                              if (derived.stateCode) {
                                form.setFieldsValue({
                                  stateCode: derived.stateCode,
                                  ...(derived.stateName ? { stateName: derived.stateName } : {}),
                                });
                              }
                            }
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={10}>
                      <Form.Item label="GST Rate (%)" name="gstPercentage">
                        <InputNumber min={0} max={100} style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={14}>
                      <Form.Item label="State Name" name="stateName" tooltip="State where company is registered for GST">
                        <Select
                          showSearch
                          placeholder="Select State"
                          options={GST_STATE_OPTIONS}
                          filterOption={(input, option) =>
                            (option?.label ?? "").toLowerCase().includes(input.toLowerCase()) ||
                            (option?.value ?? "").toLowerCase().includes(input.toLowerCase()) ||
                            (option?.code ?? "").includes(input)
                          }
                          onChange={(val) => {
                            const code = getStateCodeFromName(val);
                            if (code) {
                              form.setFieldsValue({ stateCode: code });
                            }
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={10}>
                      <Form.Item label="State Code" name="stateCode" tooltip="2-digit GST state code (e.g. 33)">
                        <Input
                          placeholder="e.g. 33"
                          maxLength={2}
                          onChange={(e) => {
                            const code = e.target.value?.trim();
                            if (code && GST_STATE_MAP[code]) {
                              form.setFieldsValue({ stateName: GST_STATE_MAP[code] });
                            }
                          }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item label="Registered Address for Invoices" name="invoiceRegisteredAddress">
                    <Input.TextArea rows={2} placeholder="Full legal registered address..." />
                  </Form.Item>
                  <Form.Item label="Invoice Footer Note" name="invoiceFooter">
                    <Input placeholder="Thank you for your business." />
                  </Form.Item>
                  <Form.Item label="Standard Terms & Conditions" name="invoiceTerms">
                    <Input.TextArea rows={3} placeholder="1. Payment is due within 15 days..." />
                  </Form.Item>
                </Flex>
              </Card>
            </Col>

            {/* ── QUOTATION PAGE 2 BANNER & NOTE CUSTOMIZATION ── */}
            <Col span={24}>
              <Card
                className="org-card"
                styles={{ body: { padding: 24 } }}
                title={
                  <Flex align="center" gap={10}>
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 8,
                        background: "#eff6ff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#2563eb",
                      }}
                    >
                      <PictureOutlined style={{ fontSize: 18 }} />
                    </div>
                    <span style={{ fontSize: "1.05rem", fontWeight: 700, color: "#0f172a" }}>Quotation Page 2 & Banner Customization</span>
                  </Flex>
                }
              >
                <Row gutter={24}>
                  <Col xs={24} md={12}>
                    <Flex vertical gap={14}>
                      <Form.Item
                        label="Quotation Partner Banner Tagline"
                        name="quotationBannerText"
                        tooltip="Heading text displayed inside the large partner panel on Page 2."
                      >
                        <Input.TextArea rows={2} placeholder="Think Software,\nThink Softrate." />
                      </Form.Item>

                      <div>
                        <Text style={{ fontSize: "0.75rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", display: "block", marginBottom: 8 }}>
                          Tagline Text Color
                        </Text>
                        <Flex align="center" gap={12} wrap="wrap">
                          <ColorPicker
                            value={quotationBannerTextColor}
                            onChange={(c) => {
                              const hex = typeof c === "string" ? c : c.toHexString();
                              setQuotationBannerTextColor(hex);
                              form.setFieldsValue({ quotationBannerTextColor: hex });
                            }}
                            showText
                          />
                          <Form.Item name="quotationBannerTextColor" noStyle>
                            <Input
                              value={quotationBannerTextColor}
                              onChange={(e) => setQuotationBannerTextColor(e.target.value)}
                              style={{ width: 120, fontFamily: "monospace" }}
                              placeholder="#ffffff"
                            />
                          </Form.Item>
                          <Button
                            size="small"
                            onClick={() => {
                              setQuotationBannerTextColor("#ffffff");
                              form.setFieldsValue({ quotationBannerTextColor: "#ffffff" });
                            }}
                          >
                            Reset Default (#ffffff)
                          </Button>
                        </Flex>
                      </div>

                      <div>
                        <Text style={{ fontSize: "0.75rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", display: "block", marginBottom: 8 }}>
                          Banner Background Style
                        </Text>
                        <Radio.Group
                          value={bannerBgType}
                          onChange={(e) => setBannerBgType(e.target.value)}
                          buttonStyle="solid"
                          style={{ marginBottom: 12 }}
                        >
                          <Radio.Button value="color">Background Color</Radio.Button>
                          <Radio.Button value="image">Uploaded Image (Compressed)</Radio.Button>
                        </Radio.Group>

                        {bannerBgType === "color" ? (
                          <Flex align="center" gap={12} wrap="wrap">
                            <ColorPicker
                              value={quotationBannerBgColor}
                              onChange={(c) => {
                                const hex = typeof c === "string" ? c : c.toHexString();
                                setQuotationBannerBgColor(hex);
                                form.setFieldsValue({ quotationBannerBgColor: hex });
                              }}
                              showText
                            />
                            <Form.Item name="quotationBannerBgColor" noStyle>
                              <Input
                                value={quotationBannerBgColor}
                                onChange={(e) => setQuotationBannerBgColor(e.target.value)}
                                style={{ width: 120, fontFamily: "monospace" }}
                                placeholder="#785b18"
                              />
                            </Form.Item>
                            <Button
                              size="small"
                              onClick={() => {
                                setQuotationBannerBgColor("#785b18");
                                form.setFieldsValue({ quotationBannerBgColor: "#785b18" });
                              }}
                            >
                              Reset Default (#785b18)
                            </Button>
                          </Flex>
                        ) : (
                          <Flex align="center" gap={12} wrap="wrap">
                            <Upload
                              beforeUpload={(file) => {
                                handleBannerBgImageUpload(file);
                                return false;
                              }}
                              showUploadList={false}
                              accept="image/*"
                            >
                              <Button icon={<UploadOutlined />} style={{ borderColor: "#2563eb", color: "#2563eb", fontWeight: 600 }}>
                                {quotationBannerBgImage ? "Replace Compressed Image" : "Upload & Auto-Compress Image"}
                              </Button>
                            </Upload>
                            {quotationBannerBgImage && (
                              <Button
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => {
                                  setQuotationBannerBgImage("");
                                  setBannerBgType("color");
                                }}
                              >
                                Remove Image & Use Color
                              </Button>
                            )}
                          </Flex>
                        )}
                      </div>

                      <Form.Item
                        label="Quotation Mission & Tax Bottom Note"
                        name="quotationNote"
                        tooltip="Appears at the bottom of Page 2 below the bank details and terms."
                      >
                        <Input.TextArea
                          rows={3}
                          placeholder="We aim to provide the best software to automate your business (Our charges are 50% less than market price with a mantra “High quality at affordable cost”).\nThe price mentioned above is exclusive of applicable tax."
                        />
                      </Form.Item>
                    </Flex>
                  </Col>

                  <Col xs={24} md={12}>
                    <Text style={{ fontSize: "0.75rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", display: "block", marginBottom: 8 }}>
                      Live Banner Preview
                    </Text>
                    <div
                      style={{
                        minHeight: 180,
                        borderRadius: 12,
                        backgroundColor: quotationBannerBgColor || "#785b18",
                        backgroundImage: bannerBgType === "image" && quotationBannerBgImage ? `url(${quotationBannerBgImage})` : "none",
                        backgroundSize: "cover",
                        backgroundPosition: "right center",
                        display: "flex",
                        alignItems: "center",
                        padding: "24px 28px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                        border: "1px solid #cbd5e1",
                      }}
                    >
                      <div
                        style={{
                          margin: 0,
                          color: quotationBannerTextColor || "#ffffff",
                          fontSize: "24px",
                          fontWeight: 800,
                          lineHeight: 1.25,
                          whiteSpace: "pre-line",
                        }}
                      >
                        {quotationBannerTextWatched || "Think Software,\nThink Softrate."}
                      </div>
                    </div>
                    <Text type="secondary" style={{ fontSize: "0.75rem", marginTop: 6, display: "block" }}>
                      * Live preview showing how the partner banner appears on Page 2 of generated quotations.
                    </Text>
                  </Col>
                </Row>
              </Card>
            </Col>

            <Col span={24}>
              <Card
                className="org-card"
                styles={{ body: { padding: 24 } }}
                title={
                  <Flex align="center" gap={10}>
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 8,
                        background: "#eff6ff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#2563eb",
                      }}
                    >
                      <BankOutlined style={{ fontSize: 18 }} />
                    </div>
                    <span style={{ fontSize: "1.05rem", fontWeight: 700, color: "#0f172a" }}>Bank Accounts for Payments</span>
                  </Flex>
                }
              >
                <Flex vertical gap={24}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                      <span style={{ color: "#2563eb", fontWeight: 700, fontSize: "0.75rem", letterSpacing: "0.03em" }}>
                        PRIMARY (GST INVOICES)
                      </span>
                      <Text strong style={{ fontSize: "0.9rem", color: "#0f172a" }}>
                        Standard Bank Account
                      </Text>
                      <Text type="secondary" style={{ fontSize: "0.78rem" }}>
                        — Used for standard invoices and clients with GSTIN
                      </Text>
                    </div>
                    <Row gutter={16}>
                      <Col xs={24} sm={12} md={6}>
                        <Form.Item label="Bank Name" name="bankName">
                          <Input placeholder="HDFC Bank" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={6}>
                        <Form.Item label="Account Number" name="accountNumber">
                          <Input placeholder="50200012345678" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={6}>
                        <Form.Item label="IFSC Code" name="ifscCode">
                          <Input placeholder="HDFC0001234" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={6}>
                        <Form.Item label="Branch Name" name="branchName">
                          <Input placeholder="Chennai Main Branch" />
                        </Form.Item>
                      </Col>
                    </Row>
                  </div>
                  <div style={{ height: 1, background: "#e2e8f0" }} />
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                      <span style={{ color: "#16a34a", fontWeight: 700, fontSize: "0.75rem", letterSpacing: "0.03em" }}>
                        NON-GST BANK ACCOUNT
                      </span>
                      <Text strong style={{ fontSize: "0.9rem", color: "#0f172a" }}>
                        Secondary Bank Account
                      </Text>
                      <Text type="secondary" style={{ fontSize: "0.78rem" }}>
                        — Automatically shown on invoices & quotations when client has no GST
                      </Text>
                    </div>
                    <Row gutter={16}>
                      <Col xs={24} sm={12} md={6}>
                        <Form.Item label="Bank Name" name="bankName2">
                          <Input placeholder="e.g. ICICI Bank / State Bank of India" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={6}>
                        <Form.Item label="Account Number" name="accountNumber2">
                          <Input placeholder="e.g. 100029384812" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={6}>
                        <Form.Item label="IFSC Code" name="ifscCode2">
                          <Input placeholder="e.g. ICIC0000123" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={6}>
                        <Form.Item label="Branch Name" name="branchName2">
                          <Input placeholder="e.g. T. Nagar Branch" />
                        </Form.Item>
                      </Col>
                    </Row>
                  </div>
                </Flex>
              </Card>
            </Col>

            <Col span={24}>
              <Card
                className="org-card"
                styles={{ body: { padding: 24 } }}
                title={
                  <Flex align="center" gap={10}>
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 8,
                        background: "#eff6ff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#2563eb",
                      }}
                    >
                      <AppstoreOutlined style={{ fontSize: 18 }} />
                    </div>
                    <span style={{ fontSize: "1.05rem", fontWeight: 700, color: "#0f172a" }}>Services & Product Catalog ({services.length})</span>
                  </Flex>
                }
              >
                <Flex vertical gap={16}>
                  {/* Add New Service Row */}
                  <div
                    style={{
                      padding: 16,
                      background: "#f8fafc",
                      borderRadius: 10,
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    <Row gutter={[12, 12]} align="middle">
                      <Col xs={24} sm={8}>
                        <Input placeholder="Service Name (e.g. CRM Integration)" value={newServiceName} onChange={(e) => setNewServiceName(e.target.value)} />
                      </Col>
                      <Col xs={12} sm={4}>
                        <Input placeholder="HSN/SAC" value={newSacHsn} onChange={(e) => setNewSacHsn(e.target.value)} />
                      </Col>
                      <Col xs={12} sm={4}>
                        <InputNumber min={0} placeholder="Min Price (₹)" value={newMinPrice ?? undefined} onChange={setNewMinPrice} formatter={amountFormatter} parser={amountParser} style={{ width: "100%" }} />
                      </Col>
                      <Col xs={12} sm={4}>
                        <InputNumber min={0} placeholder="Max Price (₹)" value={newMaxPrice ?? undefined} onChange={setNewMaxPrice} formatter={amountFormatter} parser={amountParser} style={{ width: "100%" }} />
                      </Col>
                      <Col xs={24} sm={4}>
                        <Button type="primary" icon={editingServiceId ? <EditOutlined /> : <PlusOutlined />} onClick={handleAddService} block>
                          {editingServiceId ? "Update" : "Add Service"}
                        </Button>
                      </Col>
                    </Row>
                  </div>

                  <Table
                    columns={serviceColumns}
                    dataSource={services}
                    rowKey="id"
                    pagination={{ pageSize: 5 }}
                    size="small"
                    expandable={{
                      expandedRowRender: (record) => <SubProductsNestedView service={record} onAddSubProduct={handleAddSubProduct} onDeleteSubProduct={handleDeleteSubProduct} />,
                      rowExpandable: () => true,
                    }}
                  />
                </Flex>
              </Card>
            </Col>
          </Row>
        </Form>
      </Spin>
    </div>
  );
}

export default InvoiceSettings;
